import React from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

interface Props {
  open: boolean;
  /** Mobile → render as a full-screen page (back arrow); desktop → centered modal. */
  asPage?: boolean;
  title: string;
  /** Optional icon shown beside the title. */
  icon?: React.ReactNode;
  onClose: () => void;
  /** Desktop modal width (Tailwind class). */
  maxWidth?: string;
  children: React.ReactNode;
}

/**
 * A panel that is a centered modal on desktop and a full-screen page on mobile — used
 * for Invite People / Manage Group so they feel like native pages on a phone.
 */
const SheetOrPage: React.FC<Props> = ({ open, asPage, title, icon, onClose, maxWidth = 'max-w-md', children }) => {
  if (!open) return null;

  const header = (back: boolean) => (
    <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-600">
      <div className="flex items-center gap-2">
        {back && (
          <button onClick={onClose} aria-label="Back" title="Back" className="text-secondary-500 hover:text-secondary-900 dark:hover:text-white"><ChevronLeft className="h-5 w-5" /></button>
        )}
        {icon}
        <h2 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{title}</h2>
      </div>
      {!back && (
        <button onClick={onClose} aria-label="Close" title="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
      )}
    </div>
  );

  if (asPage) {
    // h-[100dvh] (dynamic viewport) — NOT inset-0/h-screen. On mobile Safari a fixed
    // inset-0 element's bottom aligns to the LARGE viewport, so it sits behind the
    // address bar/toolbar and hides bottom action buttons. dvh tracks the VISIBLE
    // height; the safe-area pad keeps the last control clear of the iOS home bar.
    return (
      <div className="fixed inset-x-0 top-0 z-50 flex h-[100dvh] flex-col bg-white dark:bg-secondary-900" data-testid="sheet-page">
        {header(true)}
        <div className="flex-1 overflow-y-auto p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">{children}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="sheet-modal">
      <div className={`w-full ${maxWidth} rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-600 shadow-xl flex flex-col max-h-[88dvh]`} onClick={(e) => e.stopPropagation()}>
        {header(false)}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

export default SheetOrPage;
